import { useRef } from 'react';

/**
 * Custom hook that performs deep comparison for useMemo dependencies
 * to prevent unnecessary recalculations when object contents are the same
 */
export function useDeepMemo<T>(factory: () => T, deps: any[]): T {
  const ref = useRef<{ deps: any[]; value: T } | null>(null);
  
  if (!ref.current || !deepEqual(ref.current.deps, deps)) {
    ref.current = {
      deps: [...deps],
      value: factory()
    };
  }
  
  return ref.current.value;
}

/**
 * Deep equality check for simple objects and arrays
 */
function deepEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (typeof a !== typeof b) return false;
  
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false;
    return a.every((item, index) => deepEqual(item, b[index]));
  }
  
  if (typeof a === 'object') {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    return keysA.every(key => deepEqual(a[key], b[key]));
  }
  
  return false;
}

/**
 * Stable object reference hook - returns the same object reference
 * if the contents haven't changed using deep comparison
 */
export function useStableObject<T extends object>(obj: T): T {
  return useDeepMemo(() => obj, [obj]);
}

/**
 * Stable array reference hook - returns the same array reference
 * if the contents haven't changed using deep comparison
 */
export function useStableArray<T>(arr: T[]): T[] {
  return useDeepMemo(() => arr, [arr]);
}
